import { useState, useCallback, useSyncExternalStore } from 'react'
import { trackEvent } from '../utils/analytics'
import { isStandalone } from '../utils/standalone'

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

interface InstallPromptState {
  canInstall: boolean
  isInstalled: boolean
  isIOS: boolean
  install: () => Promise<void>
  dismiss: () => void
  dismissed: boolean
}

const DISMISS_KEY = 'bt_install_dismissed_at'
const DISMISS_DAYS = 7

function isDismissed(): boolean {
  try {
    const raw = localStorage.getItem(DISMISS_KEY)
    if (!raw) return false
    const dismissedAt = parseInt(raw, 10)
    const daysSince = (Date.now() - dismissedAt) / (1000 * 60 * 60 * 24)
    return daysSince < DISMISS_DAYS
  } catch {
    return false
  }
}

/**
 * iOS/iPadOS, where the platform — not the browser choice — decides what a web
 * app may do. Exported because push has the same gate: iOS grants the Push API
 * to installed home-screen apps only.
 */
export function isIOSSafari(): boolean {
  const ua = navigator.userAgent
  return /iPad|iPhone|iPod/.test(ua) && !('MSStream' in window)
}

// ─────────────────────────────────────────────────────────────────────────────
// Module-level store
//
// Chrome fires `beforeinstallprompt` once, early in page load — usually before
// anything that wants it has mounted. InstallBanner in particular only renders
// once you are signed in, so on the landing page nothing would be listening and
// the event would be lost for the rest of that session (visible when signing in
// with an OTP code, which never reloads the page).
//
// Capturing at module scope decouples *catching* the event from *showing* the
// UI: the listener is installed as soon as this module is imported, and any
// hook that mounts later reads what was already caught.
// ─────────────────────────────────────────────────────────────────────────────

let capturedPrompt: BeforeInstallPromptEvent | null = null
let installed = false
const listeners = new Set<() => void>()

function emit(): void {
  listeners.forEach((listener) => listener())
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

const getPrompt = () => capturedPrompt
const getInstalled = () => installed

if (typeof window !== 'undefined') {
  installed = isStandalone()

  window.addEventListener('beforeinstallprompt', (e: Event) => {
    // Suppress the browser's own prompt — we surface our own banner instead.
    e.preventDefault()
    capturedPrompt = e as BeforeInstallPromptEvent
    emit()
  })

  window.addEventListener('appinstalled', () => {
    installed = true
    capturedPrompt = null
    emit()
    trackEvent('btv_pwa_install_completed')
  })

  window
    .matchMedia('(display-mode: standalone)')
    .addEventListener('change', (e: MediaQueryListEvent) => {
      installed = e.matches
      emit()
    })
}

export function useInstallPrompt(): InstallPromptState {
  const deferredPrompt = useSyncExternalStore(subscribe, getPrompt)
  const isInstalled = useSyncExternalStore(subscribe, getInstalled)
  // Dismissal is per-browser and read once; it never changes underneath us.
  const [dismissed, setDismissed] = useState(isDismissed)
  const isIOS = isIOSSafari()

  const install = useCallback(async () => {
    const prompt = capturedPrompt
    if (!prompt) return

    await prompt.prompt()
    const { outcome } = await prompt.userChoice

    // The event is one-shot — it cannot be re-prompted whatever the outcome.
    capturedPrompt = null
    if (outcome === 'accepted') installed = true
    emit()
  }, [])

  const dismiss = useCallback(() => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()))
    setDismissed(true)
  }, [])

  const canInstall = !isInstalled && (!!deferredPrompt || isIOS) && !dismissed

  return { canInstall, isInstalled, isIOS, install, dismiss, dismissed }
}
